/* This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at http://mozilla.org/MPL/2.0/. */

// AUTOGENERATED BY glean_parser v19.2.0. DO NOT EDIT. DO NOT COMMIT.

import EventMetricType from '@mozilla/glean/private/metrics/event';

/**
 * Passkey sign-in completed end-to-end on the frontend. Fires after the
 * WebAuthn ceremony succeeds AND any subsequent Sync password step
 * (creating a new password or entering an existing one) completes. Does
 * not fire if the user abandons during a password step. Distinct from
 * the per-surface `*_passkey_submit_success` events, which fire at
 * ceremony-verified time and don't reflect the full journey outcome.
 *
 * Generated from `passkey.auth_success`.
 */
export const authSuccess = new EventMetricType<{
  reason?: string;
}>(
  {
    category: 'passkey',
    name: 'auth_success',
    sendInPings: ['events'],
    lifetime: 'ping',
    disabled: false,
  },
  ['reason']
);

/**
 * The "Sign in with passkey" button was shown on a sign-in surface. Fires
 * once per surface render (only when the passkey feature flags are on), so
 * click-through to the per-surface `*_passkey_submit` event is measurable.
 *
 * Generated from `passkey.button_view`.
 */
export const buttonView = new EventMetricType<{
  reason?: string;
}>(
  {
    category: 'passkey',
    name: 'button_view',
    sendInPings: ['events'],
    lifetime: 'ping',
    disabled: false,
  },
  ['reason']
);

/**
 * The help link in the passkey sign-in trouble banner was clicked. The
 * banner is shown for a cancelled ceremony (dismissed prompt, no passkey on
 * this device, or the authenticator can't satisfy the request).
 *
 * Generated from `passkey.get_help_link_click`.
 */
export const getHelpLinkClick = new EventMetricType<{
  reason?: string;
}>(
  {
    category: 'passkey',
    name: 'get_help_link_click',
    sendInPings: ['events'],
    lifetime: 'ping',
    disabled: false,
  },
  ['reason']
);

/**
 * Records whether the WebAuthn PRF extension produced an output at passkey
 * sign-in (`navigator.credentials.get()`), i.e. whether the credential
 * supports PRF on this device. Early telemetry ahead of passwordless Sync
 * (Phase 2) to learn the true PRF capability of the passkey base, which the
 * registration-time `prfEnabled` flag under-reports. Only the presence of
 * the PRF output is recorded — never the output itself.
 *
 * Generated from `passkey.signin_prf_support`.
 */
export const signinPrfSupport = new EventMetricType<{
  supported?: string;
}>(
  {
    category: 'passkey',
    name: 'signin_prf_support',
    sendInPings: ['events'],
    lifetime: 'ping',
    disabled: false,
  },
  ['supported']
);

/**
 * A passkey sign-in's first WebAuthn attempt failed in a way the optional
 * PRF extension could have caused (e.g. Windows Hello UnknownError,
 * FXA-13991), so the ceremony silently retried once without PRF. Mirrors
 * `account_pref.passkey_create_retry_without_prf_request` for the sign-in
 * (`navigator.credentials.get()`) path.
 *
 * Generated from `passkey.signin_retry_without_prf_request`.
 */
export const signinRetryWithoutPrfRequest = new EventMetricType<{
  outcome?: string;
  reason?: string;
}>(
  {
    category: 'passkey',
    name: 'signin_retry_without_prf_request',
    sendInPings: ['events'],
    lifetime: 'ping',
    disabled: false,
  },
  ['outcome', 'reason']
);
